import React from 'react';
import CodeBlock from './CodeBlock';
import EmailDraftCard, { parseEmailContent, isFullEmail } from './EmailDraftCard';
import InteractiveChart from './InteractiveChart';

/**
 * Renders CodeBlock for normal code, EmailDraftCard for full verbatim emails only
 * (```email/```gmail/```outlook or plain code blocks that parse as full emails),
 * or InteractiveChart for ```mermaid blocks.
 * During streaming: keep email content in markdown/code-block form.
 * After streaming completes: swap to EmailDraftCard for full emails.
 */
const CodeBlockWithEmail = ({ language, children, darkMode, onSend, sendDraftLoading, sentDrafts = [], chatTitle, isStreaming, onOpenArtifact, messageId, chatId, onDraftSent, onSaveDraftContent, currentMessageText, responseIndex }) => {
  const isMermaidBlock = language === 'mermaid';
  if (isMermaidBlock) {
    return <InteractiveChart darkMode={darkMode} chatTitle={chatTitle} isStreaming={isStreaming}>{children}</InteractiveChart>;
  }

  const rawContent = typeof children === 'string' ? children :
    (Array.isArray(children) ? children.join('') : String(children || ''));
  const parsed = parseEmailContent(rawContent);

  const isExplicitEmailBlock = language === 'email' || language === 'gmail' || language === 'outlook';
  const isPlainBlockWithFullEmail = (!language || language === 'text') && isFullEmail(parsed);
  // During streaming, never mount EmailDraftCard; keep markdown/code block rendering stable.
  if (isStreaming && (isExplicitEmailBlock || isPlainBlockWithFullEmail)) {
    return <CodeBlock language={language} darkMode={darkMode}>{children}</CodeBlock>;
  }

  if ((isExplicitEmailBlock || isPlainBlockWithFullEmail) && isFullEmail(parsed)) {
    const emailSource = language === 'gmail' || language === 'outlook' ? language : (parsed.source || 'email');
    const resolvedSource = emailSource === 'gmail' || emailSource === 'outlook' ? emailSource : 'gmail';
    const onEdit = onOpenArtifact ? () => {
      onOpenArtifact({
        type: 'email',
        messageId,
        chatId,
        to: parsed.to,
        from: parsed.from,
        subject: parsed.subject,
        body: parsed.body,
        rawContent,
        emailSource: resolvedSource,
        onSend,
        sendDraftLoading,
        sentDrafts,
        onDraftSent,
        onSave: onSaveDraftContent ? (draft) => onSaveDraftContent(rawContent, draft, currentMessageText, responseIndex, language || '') : undefined,
      });
    } : undefined;
    return (
      <EmailDraftCard
        rawContent={rawContent}
        darkMode={darkMode}
        emailSource={emailSource}
        onEdit={onEdit}
        onSend={onSend}
        sendDraftLoading={sendDraftLoading}
        sentDrafts={sentDrafts}
      />
    );
  }

  return <CodeBlock language={language} darkMode={darkMode}>{children}</CodeBlock>;
};

export default CodeBlockWithEmail;
